import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import {
  Class,
  ClassNote,
  Lesson,
  LessonEnrolment,
} from './types';

export const classesApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    newClass: build.mutation<any, {
      subject: string,
      course: string,
      stream: string,
      location: string,
      id: string,
      day?: number,
      time?: string,
    }>({
      query(arg) {
        const { subject, course, stream, location, id, day, time } = arg;

        return {
          url: 'classes/new',
          body: {
            subject,
            course,
            stream,
            location,
            id,
            day,
            time,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['Classes'],
    }),
    getClass: build.query<CognitoAPIResponse<Class>, {
      subject: string,
      classId: string }>({
      query(arg) {
        const {
          subject,
          classId,
        } = arg;
        return {
          url: `classes/${subject}/${classId}`,
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [{
          type: 'Classes', id: `${result.data.id}`,
        }]
        : ['Lessons']),
      transformResponse(response: any) {
        return attachId(response, (entry: any) =>
          `${entry.subject}#${entry.classId}`,
        );
      },
    }),
    getClasses: build.query<CognitoAPIResponse<Class[]>, {
      subject?: string,
      course?: string,
      stream?: string,
      day?: number,
      location?: string,
      tag?: string,
      school?: string,
      students?: string,
      teacher?: string,
      active?: boolean,
      includeNextLessonDatetime?: boolean }>({
      query(arg) {
        const {
          subject,
          course,
          stream,
          day,
          location,
          tag,
          school,
          teacher,
          students,
          active,
          includeNextLessonDatetime,
        } = arg;
        return {
          url: 'classes/query',
          method: 'GET',
          params: {
            subject,
            course,
            stream,
            day,
            location,
            tag,
            school,
            students,
            active,
            teacher,
            includeNextLessonDatetime,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Classes' as const, id })), 'Classes']
        : ['Classes']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) =>
          `${entry.subject}#${entry.classId}`,
        );
      },
    }),
    updateClass: build.mutation<any, {
      subject: string,
      classId: string,
      newAttributes: any,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          newAttributes,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Classes', id: `${arg.subject}#${arg.classId}` }],
    }),
    archiveClass: build.mutation<any, {
      subject: string,
      classId: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/archive`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Classes', id: `${arg.subject}#${arg.classId}` }],
    }),
    graduateClass: build.mutation<any, {
      subject: string,
      classId: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/graduate`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Classes', id: `${arg.subject}#${arg.classId}` }],
    }),
    reviveClass: build.mutation<any, {
      subject: string,
      classId: string
    }>({
      query(arg) {
        const {
          subject,
          classId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/revive`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Classes', id: `${arg.subject}#${arg.classId}` }],
    }),
    enrolToClass: build.mutation<any, {
      subject: string,
      classId: string,
      studentId: string,
      enrolFromDatetime: number,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          studentId,
          enrolFromDatetime,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/enrol`,
          body: {
            studentId,
            enrolFromDatetime,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    unenrolFromClass: build.mutation<any, {
      subject: string,
      classId: string,
      studentId: string,
      unenrolFromDatetime: number,
      reasonObj?: {
        reason: string,
        reasonType: string,
        reasonCategory: string
      }
    }>({
      query(arg) {
        const {
          subject,
          classId,
          studentId,
          unenrolFromDatetime,
          reasonObj,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/unenrol`,
          body: {
            studentId,
            unenrolFromDatetime,
            ...reasonObj,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    getClassNotes: build.query<CognitoAPIResponse<ClassNote[]>, {
      subject: string,
      classId: string,
      active?: boolean,
      sticky?: boolean}>({
      query(arg) {
        const {
          subject,
          classId,
          active,
          sticky,
        } = arg;
        return {
          url: `classes/${subject}/${classId}/notess/query`,
          method: 'GET',
          params: {
            active, sticky,
          },
        };
      },
      providesTags: (result, error, arg) => [{ type: 'ClassNotes', id: `${arg.subject}#${arg.classId}` }],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) =>
          `${entry.subject}#${entry.classId}`,
        );
      },
    }),
    newClassNote: build.mutation<any, {
      subject: string,
      classId: string,
      note: string,
      sticky?: boolean
    }>({
      query(arg) {
        const {
          subject,
          classId,
          note,
          sticky,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/notes/new`,
          body: {
            note,
            sticky,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'ClassNotes', id: `${arg.subject}#${arg.classId}` }],
    }),
    updateClassNote: build.mutation<any, {
      subject: string,
      classId: string,
      creationDatetime: number,
      newAttributes: any,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          creationDatetime,
          newAttributes,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'ClassNotes', id: `${arg.subject}#${arg.classId}` }],
    }),
    addLesson: build.mutation<any, {
      subject: string,
      classId: string,
      course: string,
      lessonId: string,
      startDatetime: number,
      attributes?: any,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          course,
          lessonId,
          startDatetime,
          attributes,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/add`,
          body: {
            course,
            lessonId,
            startDatetime,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
      ],
    }),
    addLessonsBatch: build.mutation<any, {
      subject: string,
      classId: string,
      startDatetime: number,
      lessonId?: string,
      attributes?: any,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lessonId,
          startDatetime,
          attributes,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/batchAdd`,
          body: {
            lessonId,
            startDatetime,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
      ],
    }),
    removeLesson: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
      ],
    }),
    removeAllFutureLessons: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/all`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
      ],
    }),
    markRoll: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/mark`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
        ...(result?.data.students || [])
          .reduce((acc: { type: string, id: string }[], studentId: string) =>
            [...acc, { type: 'Students', id: studentId }, { type: 'LessonEnrolments', id: studentId }], []),
      ],
    }),
    unmarkRoll: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/unmark`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
        ...(result?.data.students || [])
          .reduce((acc: { type: string, id: string }[], studentId: string) =>
            [...acc, { type: 'Students', id: studentId }, { type: 'LessonEnrolments', id: studentId }], []),
      ],
    }),
    enrolToLesson: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
      studentId: string,
      params?: {
        enrolmentMethod?: string,
        attendanceType?: string,
      }
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
          studentId,
          params,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/${studentId}/enrol`,
          body: params,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    unenrolFromLesson: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
      studentId: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
          studentId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/${studentId}/unenrol`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    rescheduleLesson: build.mutation<any, {
      subject: string,
      oldClassId: string,
      newClassId: string,
      oldLessonId: string,
      newLessonId: string,
      studentId: string,
    }>({
      query(arg) {
        const {
          subject,
          oldClassId,
          newClassId,
          oldLessonId,
          newLessonId,
          studentId,
        } = arg;

        return {
          url: `classes/${subject}/${oldClassId}/lessons/${encodeURIComponent(oldLessonId)}/${studentId}/reschedule/${newClassId}/${encodeURIComponent(newLessonId)}`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.oldClassId}` },
        { type: 'Classes', id: `${arg.subject}#${arg.newClassId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Lessons', id: `${arg.subject}#${arg.oldClassId}#${arg.oldLessonId}` },
        { type: 'Lessons', id: `${arg.subject}#${arg.newClassId}#${arg.newLessonId}` },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    getLessons: build.query<CognitoAPIResponse<Lesson[]>, {
      subject?: string,
      classId?: string,
      queryStartDatetime?: number,
      queryEndDatetime?: number,
      course?: string,
      lessonId?: string,
      rollMarked?: boolean,
      location?: string }>({
      query(arg) {
        const {
          subject,
          classId,
          queryStartDatetime,
          queryEndDatetime,
          course,
          lessonId,
          rollMarked,
          location,
        } = arg;
        return {
          url: 'classes/lessons/query',
          method: 'GET',
          params: {
            subject,
            classId,
            queryStartDatetime,
            queryEndDatetime,
            course,
            lessonId,
            rollMarked,
            location,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.reduce((acc, { id, subjectClassIdentifier }) =>
          [
            ...acc,
            ({ type: 'Lessons' as const, id }),
            // Invalidate lessons when their parent class changes (eg. when a student is enrolled)
            ({ type: 'Classes' as const, id: subjectClassIdentifier }),
          ], [] as any[]), 'Lessons']
        : ['Lessons']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) =>
          `${entry.subjectClassIdentifier}#${entry.lesson}`,
        );
      },
    }),
    getLesson: build.query<CognitoAPIResponse<Lesson>, {
      subject: string,
      classId: string,
      lesson: string }>({
      query(arg) {
        const { subject, classId, lesson } = arg;
        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [
        { type: 'Lessons', id: result?.data.id },
        // Invalidate lesson when its parent class changes (eg. when a student is enrolled)
        { type: 'Classes', id: result?.data.subjectClassIdentifier },
      ],
      transformResponse(response: any) {
        return attachId(response, (entry: Lesson) =>
          `${entry.subjectClassIdentifier}#${entry.lesson}`,
        );
      },
    }),
    updateLesson: build.mutation<any,
    {
      subject: string,
      classId: string,
      lesson: string,
      newAttributes: any
    }>({
      query(arg) {
        const { subject, classId, lesson, newAttributes } = arg;
        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
      ],
    }),
    addBreak: build.mutation<any,
    {
      subject: string,
      classId: string,
      weeks: number,
      startDatetime: number
    }>({
      query(arg) {
        const { subject, classId, weeks, startDatetime } = arg;
        return {
          url: `classes/${subject}/${classId}/breaks/add`,
          body: {
            weeks,
            startDatetime,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
      ],
    }),
    updateLessonEnrolment: build.mutation<any, {
      subject: string,
      classId: string,
      lesson: string,
      studentId: string,
      newAttributes: any
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
          studentId,
          newAttributes,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/${studentId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
      ],
    }),
    getLessonEnrolment: build.query<CognitoAPIResponse<LessonEnrolment>, {
      subject: string,
      classId: string,
      lesson: string,
      studentId: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          lesson,
          studentId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/${studentId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'LessonEnrolments', id: arg.studentId },
        { type: 'Lessons', id: `${arg.subject}#${arg.classId}#${arg.lesson}` },
      ],
      transformResponse(response: any) {
        return attachId(response, (entry: LessonEnrolment) =>
          `${entry.student}#${entry.classLessonIdentifier}`,
        );
      },
    }),
    getStudentLessonEnrolments: build.query<CognitoAPIResponse<LessonEnrolment[]>, {
      studentId: string,
    }>({
      query(arg) {
        const { studentId } = arg;
        return {
          url: `classes/${studentId}/lessons`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'LessonEnrolments', id: arg.studentId },
      ],
      transformResponse(response: any) {
        return attachIds(response, (entry: LessonEnrolment) =>
          `${entry.student}#${entry.classLessonIdentifier}`,
        );
      },
    }),
    getStudentDetailsByClasses: build.query<CognitoAPIResponse<string[]>, {
      classIds: string[], attribute: string,
    }>({
      query(arg) {
        const { classIds, attribute } = arg;
        return {
          url: 'classes/studentDetails',
          method: 'GET',
          params: {
            classIds: classIds.join(','),
            attribute,
          },
        };
      },
    }),
    getStudentDetailsByLesson: build.query<CognitoAPIResponse<string[]>, {
      subject: string,
      classId: string,
      lesson: string,
      attribute: string,
    }>({
      query(arg) {
        const { subject, classId, lesson, attribute } = arg;
        return {
          url: `classes/${subject}/${classId}/lessons/${encodeURIComponent(lesson)}/studentDetails`,
          method: 'GET',
          params: {
            attribute,
          },
        };
      },
    }),
    addToWaitlist: build.mutation<any, {
      subject: string,
      classId: string,
      studentId: string,
      addedDatetime: number,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          studentId,
          addedDatetime,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/waitlist/add`,
          body: {
            studentId,
            addedDatetime,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    removeFromWaitlist: build.mutation<any, {
      subject: string,
      classId: string,
      studentId: string,
    }>({
      query(arg) {
        const {
          subject,
          classId,
          studentId,
        } = arg;

        return {
          url: `classes/${subject}/${classId}/waitlist/remove`,
          body: {
            studentId,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
        { type: 'Students', id: arg.studentId },
      ],
    }),
    updateWaitlist: build.mutation<any, {
      subject: string,
      classId: string,
      waitlist: string[],
    }>({
      query(arg) {
        const { subject, classId, waitlist } = arg;

        return {
          url: `classes/${subject}/${classId}/waitlist/update`,
          body: { waitlist },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Classes', id: `${arg.subject}#${arg.classId}` },
      ],
    }),
  }),
});

export const {
  useLazyGetLessonQuery,
  useLazyGetStudentDetailsByClassesQuery,
  useGetStudentDetailsByClassesQuery,
  useGetLessonsQuery,
  useGetStudentDetailsByLessonQuery,
  useGetLessonQuery,
  useUpdateLessonMutation,
  useUpdateLessonEnrolmentMutation,
  useGetLessonEnrolmentQuery,
  useAddLessonMutation,
  useRemoveLessonMutation,
  useRemoveAllFutureLessonsMutation,
  useMarkRollMutation,
  useUnmarkRollMutation,
  useEnrolToLessonMutation,
  useUnenrolFromLessonMutation,
  useGetStudentLessonEnrolmentsQuery,
  useGetClassesQuery,
  useGetClassQuery,
  useNewClassMutation,
  useUpdateClassMutation,
  useArchiveClassMutation,
  useGraduateClassMutation,
  useReviveClassMutation,
  useEnrolToClassMutation,
  useUnenrolFromClassMutation,
  useGetClassNotesQuery,
  useNewClassNoteMutation,
  useUpdateClassNoteMutation,
  useRescheduleLessonMutation,
  useAddLessonsBatchMutation,
  useAddBreakMutation,
  useAddToWaitlistMutation,
  useRemoveFromWaitlistMutation,
  useUpdateWaitlistMutation,
} = classesApi;
